import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/lib/content";
import { EASE } from "@/components/site/Reveal";

const MobileMenu = ({ onClose }) => (
  <motion.div
    data-testid="mobile-menu"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: EASE }}
    className="fixed inset-x-0 top-[75px] bottom-0 z-40 bg-[var(--ink)] px-6 pt-8 pb-8 flex flex-col overflow-y-auto xl:hidden"
  >
    {NAV_LINKS.map((l, i) => (
      <motion.div key={l.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.06, ease: EASE, duration: 0.6 }}>
        <NavLink
          to={l.to}
          end
          onClick={onClose}
          data-testid={`mobile-nav-link-${l.id}`}
          className={({ isActive }) => `block font-display font-black uppercase text-4xl sm:text-5xl py-1.5 ${isActive ? "text-[var(--sun)]" : "text-white"}`}
        >
          {l.label}
        </NavLink>
      </motion.div>
    ))}
    <a href={CONTACT.hotlineHref} data-testid="mobile-nav-call" className="mt-auto flex items-center gap-3 rounded-full bg-[var(--sun)] text-[var(--ink)] px-6 py-4 font-display font-bold uppercase tracking-wider text-lg justify-center">
      <Phone size={18} /> Call {CONTACT.hotline}
    </a>
  </motion.div>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? "shadow-[0_10px_40px_-12px_rgba(7,13,25,0.45)]" : ""}`}
      >
        <div className="mx-auto max-w-[1600px] h-[72px] lg:h-24 px-5 lg:px-10 flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo" className="shrink-0">
            <img src="/images/logo.png" alt="Carwash Solar + Batteries" className="h-12 lg:h-[78px] w-auto" />
          </Link>
          <nav className="hidden xl:flex items-center">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `group relative px-3 py-2 font-display font-bold uppercase tracking-[0.12em] text-[15px] transition-colors duration-200 ${isActive ? "text-[#0057c2]" : "text-[var(--navy)] hover:text-[#0057c2]"}`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span className={`absolute left-3 right-3 -bottom-0.5 h-[3px] bg-[var(--sun)] origin-left transition-transform duration-300 ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={CONTACT.hotlineHref} data-testid="nav-phone" className="hidden 2xl:flex items-center gap-2 font-mono text-sm text-[var(--navy)] hover:text-[#0057c2] transition-colors">
              <Phone size={15} /> {CONTACT.hotline}
            </a>
            <Link
              to="/contact"
              data-testid="nav-button-get-quote"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[var(--navy)] text-white px-5 py-3 font-display font-bold uppercase tracking-wider text-sm transition-colors duration-300 hover:bg-[#0057c2]"
            >
              Free assessment <ArrowUpRight size={16} />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              className="xl:hidden h-11 w-11 grid place-items-center rounded-full bg-[var(--navy)] text-white"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        <div className="h-[3px] stripe-hazard opacity-90" />
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
